/** @format */

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Typography,
    Chip,
    TextField,
    CircularProgress,
    Alert,
    Divider,
    Button,
} from "@mui/material";
import {
    ArrowBackOutlined,
    ReplayOutlined,
    SendOutlined,
} from "@mui/icons-material";
import { AppDispatch, RootState } from "../../redux/store/store";
import { selectActiveBusinessId } from "../../redux/selectors/collectSelector";
import {
    fetchMySupportTicket,
    addSupportTicketMessage,
    reopenSupportTicket,
    clearCurrentTicket,
} from "../../redux/slices/collectSupportSlice";
import { SupportTicketStatus } from "../../utility/types/collectTypes";
import {
    CollectPageWrapper,
    CollectHeroBanner,
    CollectHeroInner,
    CollectHeroTitle,
    CollectContentArea,
    CollectSectionCard,
    CollectSectionTitle,
    CollectSectionSubtitle,
    CollectPrimaryButton,
    CollectSecondaryButton,
} from "./styles";
import {
    supportStatusChipSx,
    supportStatusLabel,
    supportCategoryLabel,
    formatDateTime,
} from "./supportShared";

const CollectSupportTicketDetail: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { ticketId } = useParams<{ ticketId: string }>();
    const businessId = useSelector(selectActiveBusinessId);
    const { currentTicket, isLoading, isSubmitting, error, submitError } =
        useSelector((s: RootState) => s.collectSupport);

    const [reply, setReply] = useState("");

    const load = useCallback(() => {
        if (businessId && ticketId) {
            dispatch(fetchMySupportTicket({ businessId, ticketId }));
        }
    }, [businessId, ticketId, dispatch]);

    useEffect(() => {
        load();
        return () => {
            dispatch(clearCurrentTicket());
        };
    }, [load, dispatch]);

    const ticket = currentTicket?.ticket;
    const messages = currentTicket?.messages || [];

    const canReopen =
        ticket?.status === "resolved" || ticket?.status === "closed";
    const canReply = ticket && !canReopen;

    const handleReply = async () => {
        if (!businessId || !ticketId || !reply.trim()) return;
        const result = await dispatch(
            addSupportTicketMessage({
                businessId,
                ticketId,
                body: reply.trim(),
            }),
        );
        if (addSupportTicketMessage.fulfilled.match(result)) {
            setReply("");
        }
    };

    const handleReopen = async () => {
        if (!businessId || !ticketId) return;
        const result = await dispatch(
            reopenSupportTicket({ businessId, ticketId }),
        );
        if (reopenSupportTicket.fulfilled.match(result)) {
            load();
        }
    };

    return (
        <CollectPageWrapper>
            <CollectHeroBanner>
                <CollectHeroInner>
                    <Button
                        startIcon={<ArrowBackOutlined />}
                        onClick={() => navigate("/collect/support")}
                        sx={{
                            color: "rgba(255,255,255,0.8)",
                            textTransform: "none",
                            alignSelf: "flex-start",
                            px: 0,
                        }}
                    >
                        Back to Help & Support
                    </Button>
                    <CollectHeroTitle>
                        {ticket ? ticket.ticketNumber : "Ticket"}
                    </CollectHeroTitle>
                    {ticket && (
                        <Typography sx={{ color: "rgba(255,255,255,0.85)" }}>
                            {ticket.subject}
                        </Typography>
                    )}
                </CollectHeroInner>
            </CollectHeroBanner>

            <CollectContentArea>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {isLoading && !ticket ? (
                    <Box sx={{ textAlign: "center", py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : !ticket ? (
                    <CollectSectionCard>
                        <Typography>Ticket not found.</Typography>
                    </CollectSectionCard>
                ) : (
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" },
                            gap: 3,
                        }}
                    >
                        {/* ── Conversation ── */}
                        <Box>
                            <CollectSectionCard>
                                <CollectSectionTitle>
                                    Conversation
                                </CollectSectionTitle>
                                <CollectSectionSubtitle>
                                    {supportCategoryLabel(ticket.category)}
                                </CollectSectionSubtitle>

                                <Box sx={{ mt: 2 }}>
                                    {/* Original request */}
                                    <MessageBubble
                                        author={ticket.submitterName || "You"}
                                        side="right"
                                        body={ticket.description}
                                        at={ticket.createdAt}
                                    />
                                    {messages.map((m) => (
                                        <MessageBubble
                                            key={m._id}
                                            author={
                                                m.authorType === "agent"
                                                    ? m.authorName || "Support"
                                                    : m.authorName || "You"
                                            }
                                            side={
                                                m.authorType === "agent"
                                                    ? "left"
                                                    : "right"
                                            }
                                            body={m.body}
                                            at={m.createdAt}
                                            isAgent={m.authorType === "agent"}
                                        />
                                    ))}
                                    {ticket.resolutionNote && (
                                        <Alert
                                            severity="success"
                                            sx={{ mt: 2 }}
                                        >
                                            <strong>Resolution:</strong>{" "}
                                            {ticket.resolutionNote}
                                        </Alert>
                                    )}
                                </Box>

                                <Divider sx={{ my: 2 }} />

                                {submitError && (
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        {submitError}
                                    </Alert>
                                )}

                                {canReply ? (
                                    <Box>
                                        <TextField
                                            label="Add a reply"
                                            value={reply}
                                            onChange={(e) =>
                                                setReply(
                                                    e.target.value.slice(
                                                        0,
                                                        10000,
                                                    ),
                                                )
                                            }
                                            fullWidth
                                            multiline
                                            minRows={3}
                                            size="small"
                                            sx={{ mb: 1.5 }}
                                        />
                                        <CollectPrimaryButton
                                            startIcon={<SendOutlined />}
                                            onClick={handleReply}
                                            disabled={
                                                isSubmitting || !reply.trim()
                                            }
                                        >
                                            {isSubmitting ? (
                                                <CircularProgress
                                                    size={20}
                                                    color="inherit"
                                                />
                                            ) : (
                                                "Send reply"
                                            )}
                                        </CollectPrimaryButton>
                                    </Box>
                                ) : (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            flexWrap: "wrap",
                                            gap: 2,
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                color: "#6b7280",
                                                fontSize: "0.9rem",
                                            }}
                                        >
                                            This ticket is{" "}
                                            {supportStatusLabel(
                                                ticket.status as SupportTicketStatus,
                                            ).toLowerCase()}
                                            . Reopen it if you still need help.
                                        </Typography>
                                        <CollectSecondaryButton
                                            startIcon={<ReplayOutlined />}
                                            onClick={handleReopen}
                                            disabled={isSubmitting}
                                            sx={{
                                                color: "#0f7a5b",
                                                border: "1px solid rgba(30,193,149,0.4)",
                                                backgroundColor:
                                                    "rgba(30,193,149,0.08)",
                                                "&:hover": {
                                                    backgroundColor:
                                                        "rgba(30,193,149,0.16)",
                                                },
                                            }}
                                        >
                                            Reopen ticket
                                        </CollectSecondaryButton>
                                    </Box>
                                )}
                            </CollectSectionCard>
                        </Box>

                        {/* ── Status timeline ── */}
                        <Box>
                            <CollectSectionCard>
                                <CollectSectionTitle>
                                    Status
                                </CollectSectionTitle>
                                <Box sx={{ mt: 1.5, mb: 2 }}>
                                    <Chip
                                        label={supportStatusLabel(
                                            ticket.status as SupportTicketStatus,
                                        )}
                                        sx={supportStatusChipSx(
                                            ticket.status as SupportTicketStatus,
                                        )}
                                    />
                                </Box>

                                <CollectSectionTitle sx={{ mt: 2 }}>
                                    Timeline
                                </CollectSectionTitle>
                                <Box sx={{ mt: 1.5 }}>
                                    {ticket.statusHistory.map((h, idx) => (
                                        <Box
                                            key={idx}
                                            sx={{
                                                display: "flex",
                                                gap: 1.5,
                                                mb: 1.5,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 10,
                                                    height: 10,
                                                    borderRadius: "50%",
                                                    backgroundColor: "#1ec195",
                                                    mt: "5px",
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <Box>
                                                <Typography
                                                    sx={{
                                                        fontSize: "0.85rem",
                                                        fontWeight: 600,
                                                        color: "#1f2937",
                                                    }}
                                                >
                                                    {supportStatusLabel(
                                                        h.to as SupportTicketStatus,
                                                    )}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontSize: "0.75rem",
                                                        color: "#9ca3af",
                                                    }}
                                                >
                                                    {formatDateTime(h.at)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </CollectSectionCard>
                        </Box>
                    </Box>
                )}
            </CollectContentArea>
        </CollectPageWrapper>
    );
};

const MessageBubble: React.FC<{
    author: string;
    side: "left" | "right";
    body: string;
    at?: string;
    isAgent?: boolean;
}> = ({ author, side, body, at, isAgent }) => (
    <Box
        sx={{
            display: "flex",
            justifyContent: side === "right" ? "flex-end" : "flex-start",
            mb: 1.5,
        }}
    >
        <Box
            sx={{
                maxWidth: "80%",
                backgroundColor: isAgent
                    ? "rgba(30, 193, 149, 0.10)"
                    : "#f3f4f6",
                border: isAgent
                    ? "1px solid rgba(30,193,149,0.25)"
                    : "1px solid rgba(15,23,42,0.06)",
                borderRadius: "12px",
                padding: "12px 14px",
            }}
        >
            <Typography
                sx={{
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    color: isAgent ? "#0f7a5b" : "#6b7280",
                    mb: 0.5,
                }}
            >
                {author}
            </Typography>
            <Typography
                sx={{
                    fontSize: "0.9rem",
                    color: "#1f2937",
                    whiteSpace: "pre-wrap",
                }}
            >
                {body}
            </Typography>
            {at && (
                <Typography
                    sx={{ fontSize: "0.7rem", color: "#9ca3af", mt: 0.5 }}
                >
                    {formatDateTime(at)}
                </Typography>
            )}
        </Box>
    </Box>
);

export default CollectSupportTicketDetail;
